import { useState } from 'react';
import type { MrbBerekening } from '@fleet/shared';
import { DEFAULT_MRB_PROVINCIE, GEKALIBREERDE_PROVINCIES, berekenMrb, euro } from '@fleet/shared';
import { Button } from '../../design-system';
import { Dropdown } from '../../components/ui/Dropdown';
import { Modal } from '../../components/ui/Modal';

/**
 * Popup: work out the wegenbelasting from the RDW data instead of typing it in.
 *
 * The result is an estimate — the rijksdeel is statutory but the base the
 * provinciale opcenten are levied on is calibrated against real assessments
 * (see `berekenMrb`). So it proposes a value and the user saves it; a real
 * Belastingdienst charge always wins over this.
 */
export function MrbCalculator({
  kenteken,
  massaRijklaar,
  brandstof,
  busy,
  error,
  onMassaOphalen,
  onOpslaan,
  onCancel,
}: {
  kenteken: string;
  massaRijklaar: number | null;
  brandstof: string | null;
  busy?: boolean;
  error?: string;
  /** Fetch massa rijklaar from the RDW when we don't have it yet. */
  onMassaOphalen: () => void;
  onOpslaan: (perKwartaal: number) => void;
  onCancel: () => void;
}) {
  const [provincie, setProvincie] = useState(DEFAULT_MRB_PROVINCIE);
  const jaar = new Date().getFullYear();
  const berekening: MrbBerekening | null = berekenMrb({
    massaRijklaar,
    brandstof,
    provincie,
    jaar,
  });

  return (
    <Modal open onClose={onCancel} title="Wegenbelasting berekenen" width={520}>
      <p style={{ margin: '0 0 16px', fontSize: 13, color: '#666666', lineHeight: 1.5 }}>
        Berekend uit de RDW-gegevens van{' '}
        <strong style={{ color: '#111111', fontFamily: "'Roboto Mono', monospace" }}>
          {kenteken}
        </strong>
        : de massa rijklaar en de brandstof.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Dropdown
          label="Provincie"
          block
          value={provincie}
          onChange={setProvincie}
          options={GEKALIBREERDE_PROVINCIES.map((p) => ({ value: p, label: p }))}
        />

        {massaRijklaar == null ? (
          <div
            style={{
              padding: '14px 16px',
              border: '1px solid #E3D9CC',
              background: '#FFFFFF',
              fontSize: 13,
              color: '#666666',
              lineHeight: 1.5,
            }}
          >
            De massa rijklaar van deze auto is nog niet bekend. Die staat bij de RDW en is nodig om
            te kunnen rekenen.
            <div style={{ marginTop: 12 }}>
              <Button variant="secondary" size="sm" onClick={onMassaOphalen} disabled={busy}>
                {busy ? 'Ophalen…' : 'Massa ophalen bij de RDW'}
              </Button>
            </div>
          </div>
        ) : !berekening ? (
          <div style={{ fontSize: 13, color: '#B0543B', fontWeight: 700 }}>
            Voor {provincie} kunnen we de belasting niet berekenen.
          </div>
        ) : (
          <>
            <Regels
              rijen={[
                ['Massa rijklaar (RDW)', `${massaRijklaar} kg`],
                ['Gewichtsklasse', `${berekening.klasse} kg`],
                ['Brandstof', brandstof || '—'],
                ['Rijksdeel', euro(berekening.rijksdeel)],
                [
                  `Opcenten ${provincie} (${berekening.opcentenPercentage}%)`,
                  euro(berekening.opcenten),
                ],
                ...(berekening.elektrischFactor !== 1
                  ? ([
                      [
                        'Elektrisch-tarief',
                        `${Math.round(berekening.elektrischFactor * 100)}% (${jaar})`,
                      ],
                    ] as [string, string][])
                  : []),
              ]}
            />
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                paddingTop: 12,
                borderTop: '2px solid #111111',
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 900, color: '#111111' }}>Per kwartaal</span>
              <span style={{ fontSize: 22, fontWeight: 900, color: '#111111' }}>
                {euro(berekening.perKwartaal)}
              </span>
            </div>
            <p style={{ margin: 0, fontSize: 12, color: '#888888', lineHeight: 1.5 }}>
              Schatting. Het rijksdeel volgt de wet, maar de grondslag voor de opcenten publiceert
              de Belastingdienst niet — die is geijkt op echte aanslagen en klopte daar binnen €
              0,70 per maand. Heb je een aanslag, vul dan dat bedrag in.
            </p>
          </>
        )}

        {error && <div style={{ fontSize: 13, color: '#B0543B', fontWeight: 700 }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="ghost" onClick={onCancel}>
            Annuleren
          </Button>
          <Button
            variant="primary"
            disabled={busy || !berekening}
            onClick={() => berekening && onOpslaan(berekening.perKwartaal)}
          >
            {busy ? 'Opslaan…' : 'Overnemen'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function Regels({ rijen }: { rijen: [string, string][] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {rijen.map(([label, waarde]) => (
        <div
          key={label}
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <span style={{ fontSize: 12.5, color: '#888888' }}>{label}</span>
          <span style={{ fontSize: 14, color: '#111111', fontWeight: 600, textAlign: 'right' }}>
            {waarde}
          </span>
        </div>
      ))}
    </div>
  );
}
